import { supabase } from '@/config/supabase'
import { useAuthStore } from '@/stores/auth.store'
import type { Servico, ServicoPlano, ServicoAdicional } from '@/types/database'
import type { ListFilters, PaginatedResult } from './associado.service'

const SELECT_WITH_RELATIONS = '*, planos:veic_servico_planos(*), adicionais:veic_servico_adicionais(*)'

export const servicoService = {
    async list(filters: ListFilters = {}): Promise<PaginatedResult<Servico>> {
        const { search, page = 1, pageSize = 20 } = filters

        let query = supabase
            .schema('veicular_system')
            .from('veic_servicos')
            .select(SELECT_WITH_RELATIONS, { count: 'exact' })
            .is('deleted_at', null)
            .eq('ativo', true)
            .order('nome', { ascending: true })

        if (search) {
            query = query.or(`nome.ilike.%${search}%,descricao.ilike.%${search}%`)
        }
        const from = (page - 1) * pageSize
        query = query.range(from, from + pageSize - 1)

        const { data, count, error } = await query
        if (error) throw error
        return { data: (data || []) as Servico[], total: count || 0, page, pageSize, totalPages: Math.ceil((count || 0) / pageSize) }
    },

    async getAll(): Promise<Servico[]> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_servicos')
            .select(SELECT_WITH_RELATIONS).eq('ativo', true).is('deleted_at', null).order('nome')
        if (error) throw error
        return (data || []) as Servico[]
    },

    // Used by public checkout page (no auth required — anon key)
    async getPublicById(id: string): Promise<Servico | null> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_servicos')
            .select(SELECT_WITH_RELATIONS)
            .eq('id', id)
            .eq('ativo', true)
            .eq('contratacao_online', true)
            .is('deleted_at', null)
            .single()
        if (error) return null
        return data as Servico
    },

    async create(input: Partial<Servico>): Promise<Servico> {
        const tenant_id = useAuthStore.getState().tenant?.id
        if (!tenant_id) throw new Error('Tenant não encontrado. Faça login novamente.')
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_servicos')
            .insert({ ...input, tenant_id } as any)
            .select().single()
        if (error) throw error
        return data as Servico
    },

    async update(id: string, input: Partial<Servico>): Promise<Servico> {
        // @ts-ignore
        const { data, error } = await supabase.schema('veicular_system').from('veic_servicos').update(input as any).eq('id', id).select().single()
        if (error) throw error
        return data as Servico
    },

    async remove(id: string): Promise<void> {
        // @ts-ignore
        const { error } = await supabase.schema('veicular_system').from('veic_servicos').update({ ativo: false } as any).eq('id', id)
        if (error) throw error
    },

    /* ── Planos CRUD ── */
    async listPlanos(servicoId: string): Promise<ServicoPlano[]> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_servico_planos')
            .select('*')
            .eq('servico_id', servicoId)
            .eq('ativo', true)
            .order('ordem', { ascending: true })
        if (error) throw error
        return (data || []) as ServicoPlano[]
    },

    async upsertPlano(input: Partial<ServicoPlano>): Promise<ServicoPlano> {
        const tenant_id = useAuthStore.getState().tenant?.id
        if (!tenant_id) throw new Error('Tenant não encontrado.')
        if (input.id) {
            const { data, error } = await supabase.schema('veicular_system')
                .from('veic_servico_planos')
                .update(input as any).eq('id', input.id).select().single()
            if (error) throw error
            return data as ServicoPlano
        }
        const { data, error } = await supabase.schema('veicular_system')
            .from('veic_servico_planos')
            .insert({ ...input, tenant_id } as any).select().single()
        if (error) throw error
        return data as ServicoPlano
    },

    async deletePlano(id: string): Promise<void> {
        const { error } = await supabase.schema('veicular_system')
            .from('veic_servico_planos')
            .update({ ativo: false } as any).eq('id', id)
        if (error) throw error
    },

    /* ── Adicionais CRUD ── */
    async listAdicionais(servicoId: string): Promise<ServicoAdicional[]> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_servico_adicionais')
            .select('*')
            .eq('servico_id', servicoId)
            .eq('ativo', true)
            .order('ordem', { ascending: true })
        if (error) throw error
        return (data || []) as ServicoAdicional[]
    },

    async upsertAdicional(input: Partial<ServicoAdicional>): Promise<ServicoAdicional> {
        const tenant_id = useAuthStore.getState().tenant?.id
        if (!tenant_id) throw new Error('Tenant não encontrado.')
        if (input.id) {
            const { data, error } = await supabase.schema('veicular_system')
                .from('veic_servico_adicionais')
                .update(input as any).eq('id', input.id).select().single()
            if (error) throw error
            return data as ServicoAdicional
        }
        const { data, error } = await supabase.schema('veicular_system')
            .from('veic_servico_adicionais')
            .insert({ ...input, tenant_id } as any).select().single()
        if (error) throw error
        return data as ServicoAdicional
    },

    async deleteAdicional(id: string): Promise<void> {
        const { error } = await supabase.schema('veicular_system')
            .from('veic_servico_adicionais')
            .update({ ativo: false } as any).eq('id', id)
        if (error) throw error
    },
}
